import {
  type LngLat,
  type MapLocationRequest,
  Button,
  FullscreenPanel,
  FullscreenPanelBody,
} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {useBinaryState} from '@yandex-delivery/hooks'
import {memo, useMemo, useState} from 'react'

import {type AddressPin} from '../../../../../../api/address'
import {useConfigContext} from '../../../../../../contexts/ConfigContext'
import {MAP_ANIMATION_DURATION_IN_MS} from '../../../../../../lib/constants'
import {isDefaultCoordinates} from '../../lib/utils'
import {AddressInfo} from '../AddressInfo/AddressInfo'
import {Map} from '../Map/Map'
import * as styles from './ButtonWithMapModal.classes'

/** Пропсы компонента ButtonWithMapModal */
type ButtonWithMapModalProps = {
  /** Является ли текущий выбираемый адрес - точкой "Откуда" */
  isSource: boolean
  /** Коллбэк на сохранение выбранного адреса */
  onSave: (addressPin: AddressPin) => void
  /** координаты адреса, который был выбран в дровере саджеста */
  pinLocation?: LngLat
}

/**
 * Компонент с кнопкой, открывающей модалку с картой для выбора адреса.
 */
export const ButtonWithMapModal = memo<ButtonWithMapModalProps>(function ButtonWithMapModal({
  isSource,
  onSave,
  pinLocation,
}) {
  const i18n = useI18n()

  const {location: currentUserLocation} = useConfigContext()

  const initialLocation = pinLocation && !isDefaultCoordinates(pinLocation) ? pinLocation : currentUserLocation

  const [mapCoordinates, setMapCoordinates] = useState<LngLat>(initialLocation)

  const [isModalOpened, openModal, closeModal] = useBinaryState()

  const initialMapLocation = useMemo<MapLocationRequest>(
    () => ({
      center: initialLocation,
      zoom: 17,
      duration: MAP_ANIMATION_DURATION_IN_MS,
    }),
    [initialLocation],
  )

  const [location, setLocation] = useState<MapLocationRequest>(initialMapLocation)

  const [addressInfoHeight, setAddressInfoHeight] = useState(0)

  return (
    <>
      <div className={styles.openButtonContainer}>
        <Button onPress={openModal} shape="circle" size="xs" variant="default">
          {i18n('bdui.address.map.open_button', 'На карте')}
        </Button>
      </div>

      <FullscreenPanel anchor="bottom" opened={isModalOpened}>
        <FullscreenPanelBody>
          <Map addressInfoHeight={addressInfoHeight} location={location} onLocationChange={setMapCoordinates}>
            <AddressInfo
              coordinates={mapCoordinates}
              isSource={isSource}
              onClose={closeModal}
              onLocationChange={setMapCoordinates}
              onSave={onSave}
              setAddressInfoHeight={setAddressInfoHeight}
              setLocation={setLocation}
            />
          </Map>
        </FullscreenPanelBody>
      </FullscreenPanel>
    </>
  )
})
